import { Icon } from "@iconify/react";
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Space,
  Typography,
  Modal,
  Select, // Import Select component
} from "antd";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { ContentWrapper } from "~/Components";
import styles from "../../Contract.module.scss";
import { ErrorMessage, Field, FieldProps, Formik } from "formik";
import { useAuth } from "~/Hooks/useAuth";
import { useNotification } from "~/Hooks/useNotification";
import { get, post, put } from "@services/api.service";
import { SuccessModal } from "@components/SuccessModal/SuccessModal";
import { useQuery } from "~/Hooks/useQuery";
import { Subsidiary } from "../../Subsidiaries/Subsidiaries";
import { allowedDomainsAll, getDivisionList } from "~/Utils";
import { find, sortBy, values } from "lodash";

interface FormItems {
  subsidiaryId: string;
  divisionAdmins: {
    name: string;
    email: string;
  }[];
  recipientLock: boolean;
  division: number | null;
}

const defaultValue = {
  subsidiaryId: "",
  divisionAdmins: [
    {
      name: "",
      email: "",
    },
  ],
  recipientLock: false,
  division: null,
} as FormItems;

export const AddNewDivision = () => {
  const { user } = useAuth();
  const { openToast, openNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [initialValue, setInitialValue] = useState<FormItems>(defaultValue);
  const [openModal, setOpenModal] = useState(false);
  const [subsidiaryValues, setSubisidiaryValues] = useState<FormItems>();
  const [divisionDetails, setDivisionDetails] = useState<any>({});
  const [successOpen, setSuccessOpen] = useState(false);
  const [selectedUpdatedMail, setSelectedUpdatedMail] = useState<any>([]);
  const [divisionList, setDivisionList] = useState<any>([]);
  const { id } = useParams();
  const query = useQuery();
  const navigate = useNavigate();
  const divisionId = query?.get("divisionId") || "";
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const viewItem = query?.get("view") || "";
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [subsidiaryId, setSubsidiaryId] = useState("");
  const [reRenderKey, setReRenderKey] = useState(0);

  useEffect(() => {
    if (divisionId) {
      setIsLoading(true);
      get(`v1/division/division-detail?divisionId=${divisionId}`)
        .then((res: any) => {
          let division = res.response.data;
          if (division) {
            setApproverData(division.subsidiaryAdmins);
          } else {
            console.error("No records found in the response");
            setApproverData([]);
          }
          setDivisionDetails(division);
          const divisonAdminData =
            division &&
            division.divisionAdmins.map((item: any) => ({
              ...item,
              name: item.name,
              email: item.email,
            }));
          const values = {
            subsidiaryId: division.subsidiaryId,
            divisionAdmins: divisonAdminData || [],
            division: division.division || null,
          } as unknown as FormItems;
          setInitialValue(values);
          setSubisidiaryValues(values);
          setIsLoading(false);
        })
        .catch(() => {
          openToast({
            content: "Getting data failed",
            type: "error",
          });
          setIsLoading(false);
        });
    }
  }, [divisionId]);

  const [subsidiaryData, setSubsidiaryData] = useState([]);

  const getSubsidiaryRecords = () => {
    const subsidiaryUrl = `v1/subsidiary/all-subsidiary`;
    get(subsidiaryUrl)
      .then((res: any) => {
        if (res && res.response && res.response.data) {
          const responseData = res.response.data;
          if (responseData) {
            setSubsidiaryData(responseData);
          } else {
            console.error("No records found in the response");
            setSubsidiaryData([]);
          }
        } else {
          console.error("Unexpected response structure:", res);
        }
      })
      .catch((err) => {
        console.error("API Error:", err);
        openToast({
          content: "Getting Subsidiary/Division failed",
          type: "error",
        });
      });
  };

  useEffect(() => {
    getSubsidiaryRecords();
  }, []);

  const onSubmit = (values: FormItems) => {
    setOpenModal(true);
    setSubisidiaryValues(values);
  };

  const [approverData, setApproverData] = useState<any>([]);

  const getUserRecords = () => {
    const subsidiaryUrl = `v1/subsidiary/subsidiary-detail?subsidiaryId=${subsidiaryId}`;
    get(subsidiaryUrl)
      .then((res: any) => {
        if (res && res.response && res.response.data) {
          const responseData = res.response.data;
          if (responseData) {
            setApproverData(responseData.subsidiaryAdmins);
          } else {
            console.error("No records found in the response");
            setApproverData([]);
          }
        } else {
          console.error("Unexpected response structure:", res);
        }
      })
      .catch((err) => {
        console.error("API Error:", err);
        openToast({
          content: "Getting Approvers failed",
          type: "error",
        });
      });
  };

  const [allApproveData, setAllApproveData] = useState([]);
  const [allApproveDataByEmail, setAllApproveDataByEmail] = useState<any>([]);

  const getUserAllRecords = () => {
    const subsidiaryUrl = `v1/user/get-admin-user-list`;
    get(subsidiaryUrl)
      .then((res: any) => {
        if (res && res.response && res.response.data) {
          const responseData = res.response.data;
          if (responseData) {
            setAllApproveData(responseData);
            setAllApproveDataByEmail(sortBy(responseData, ["email"]));
          } else {
            console.error("No records found in the response");
            setAllApproveData([]);
          }
        } else {
          console.error("Unexpected response structure:", res);
        }
      })
      .catch((err) => {
        console.error("API Error:", err);
        openToast({
          content: "Getting Approvers failed",
          type: "error",
        });
      });
  };

  useEffect(() => {
    if (subsidiaryId && !divisionId) {
      getUserRecords();
    }
    getUserAllRecords();
  }, [subsidiaryId]);

  const onHandleSubmit = async () => {
    if (!subsidiaryValues) {
      return;
    }
    const { recipientLock, ...payload } = subsidiaryValues;

    const finalPayload = {
      ...payload,
      uniqueId: divisionId,
      versionOrder: divisionDetails.versionOrder,
    };
    setIsSubmitLoading(true);
    if (divisionId) {
      put(`v1/division/update-division`, finalPayload)
        .then((res: any) => {
          if (res) {
            setSuccessOpen(true);
            setOpenModal(false)
          }
          setIsSubmitLoading(false);
        })
        .catch((err) => {
          openNotification({
            title: err,
            message: "Cost Centre updating failed",
            type: "error",
          });
          setIsSubmitLoading(false);
        });
    } else {
      post(`v1/division/create-division`, payload)
        .then((res: any) => {
          setSuccessOpen(true);
          setOpenModal(false)
          setIsSubmitLoading(false);
        })
        .catch((err) => {
          // openNotification({
          //   title: err,
          //   message: "Template Creation failed",
          //   type: "error",
          // });
          setIsSubmitLoading(false);
        });
    }
  };

  const onCancel = () => {
    setOpenModal(false);
    setIsSubmitLoading(false);
  };

  const validationSchema1 = yup.object().shape({
    subsidiaryId: yup
      .string()
      .trim()
      .required("Subsidiary/Division Name is required")
      .matches(/^\S(?:.*\S)?$/, "Subsidiary/Division Name cannot start or end with whitespace"),
    division: yup
      .string()
      .trim("Cost Centre Name cannot include leading and trailing spaces")
      .matches(/^\S(?:.*\S)?$/, "Cost Centre Name cannot start or end with whitespace")
      .required("Cost Centre Name is required"),
    divisionAdmins: yup
      .array()
      .of(
        yup.object().shape({
          name: yup
            .string()
            .trim("Name cannot include leading and trailing spaces")
            .strict(true)
            .required("Name required")
            .matches(/^[aA-zZ\s]+$/, "Invalid Name"),
          email: yup.string().strict(true).required("Email required"),
        })
      )
      .test("duplicate-email", "Duplicate email found", function (divisionAdmins) {
        if (!divisionAdmins) {
          return true;
        }

        const emailMap = new Map();
        const duplicateEmails = new Set();

        for (const divisionAdmin of divisionAdmins) {
          const { email } = divisionAdmin;
          if (emailMap.has(email)) {
            duplicateEmails.add(email);
          }
          emailMap.set(email, (emailMap.get(email) || 0) + 1);
        }

        if (duplicateEmails.size > 0) {
          const errors: any = [];

          divisionAdmins.forEach((division: any, index: any) => {
            if (duplicateEmails.has(division.email)) {
              errors.push(
                this.createError({
                  path: `divisionAdmins[${index}].email`,
                  message: "Duplicate email found",
                })
              );
            }
          });

          if (errors.length > 0) {
            throw new yup.ValidationError(errors);
          }
        }

        return true;
      }),
  });

  return (
    <>
      <ContentWrapper
        loading={isLoading}
        customTitle={
          <Space align="start">
            <Icon
              onClick={() => navigate(-1)}
              icon="akar-icons:arrow-left"
              style={{
                fontSize: "1.8rem",
                cursor: "pointer",
                color: styles.primary,
                marginRight: styles.whitespace1,
              }}
            />
            <span>
              <Typography.Title
                level={3}
                style={{ margin: 0 }}
              >
                {viewItem ? "View" : divisionId ? "Edit" : "Create"} Cost Centre
              </Typography.Title>
            </span>
          </Space>
        }
      >
        <Formik
          initialValues={initialValue}
          onSubmit={onSubmit}
          validationSchema={validationSchema1}
        >
          {(props) => {
            const { setFieldValue, setFieldTouched } = props;

            useEffect(() => {
              initialValue.divisionAdmins.forEach((division: any, index: any) => {
                if (division) {
                  setFieldValue(`divisionAdmins[${index}].email`, division.email);
                  setFieldValue(`divisionAdmins[${index}].name`, division.name);
                }
              });

              setFieldValue(`division`, initialValue.division);
              setFieldValue(`subsidiaryId`, initialValue.subsidiaryId);
            }, [initialValue]);

            useEffect(() => {
              let index = initialValue.divisionAdmins.length - 1;
              setTimeout(() => {
                setFieldTouched(`divisionAdmins[${index}].email`, false);
                setFieldTouched(`divisionAdmins[${index}].name`, false);
                setFieldTouched(`division`, false);
                setFieldTouched(`subsidiaryId`, false);
              }, 0);
            }, [divisionId, initialValue, props.values]);

            useEffect(() => {
              setSubsidiaryId(props.values.subsidiaryId);
            }, [props.values.subsidiaryId]);

            useEffect(() => {
              if (approverData && approverData.length > 0) {
                const processedDesignations = new Set();
                let validIndex = 0;
                approverData.forEach((approver: any) => {
                  setFieldValue(`divisionAdmins[${validIndex}].name`, approver?.name);
                  setFieldValue(`divisionAdmins[${validIndex}].email`, approver?.email);
                  processedDesignations.add(approver.designation);
                  validIndex++;
                });
              }
            }, [approverData]);

            const adminsMail = props.values.divisionAdmins.map((val: any) => {
              return val.email;
            });

            const adminsName = props.values.divisionAdmins.map((val: any) => {
              return val.name;
            });
            return (
              <>
                <Form
                  layout="vertical"
                  onFinish={props.handleSubmit}
                >
                  <Row style={{ marginTop: 16 }}>
                    <Col
                      xs={24}
                      md={16}
                    >
                      <div id="step1">
                        <Row>
                          <Col span={12}>
                            <div style={{ textAlign: "left" }}>
                              <Typography.Title
                                level={4}
                                style={{ marginBottom: styles.whitespace3 }}
                              >
                                Cost Centre Details
                              </Typography.Title>
                            </div>
                          </Col>
                        </Row>
                        <div>
                          <Row
                            gutter={[16, 16]}
                            align="top"
                          >
                            <Col span={8}>
                              <Form.Item label="Subsidiary/Division">
                                <Field name="subsidiaryId">
                                  {({ field, form }: FieldProps) => (
                                    <Select
                                      getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                      className={viewItem ? "viewItem" : "none"}
                                      disabled={viewItem || divisionId ? true : false}
                                      // disabled={true}
                                      showSearch
                                      value={field.value}
                                      filterOption={(input, option: any) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                      }
                                      onBlur={() => form.setFieldTouched(`subsidiaryId`, true)}
                                      onChange={(value) => {
                                        form.setFieldValue(`subsidiaryId`, value);
                                        let subsidiary: any = find(subsidiaryData, { subsidiaryId: value });
                                        setDivisionList(getDivisionList(subsidiary.name));
                                        form.setFieldValue(`division`, "");
                                      }}
                                      // status={
                                      //   form.touched.divisionAdmins?.[i]?.subsidiary && form.errors.divisionAdmins?.[i]?.subsidiary ? "error" : ""
                                      // }
                                    >
                                      {subsidiaryData.map((values: any) => (
                                        <Select.Option
                                          style={{ backgroundColor: "white" }}
                                          value={values.subsidiaryId}
                                          key={values.subsidiaryId}
                                        >
                                          {values.name}
                                        </Select.Option>
                                      ))}
                                    </Select>
                                  )}
                                </Field>
                                <Typography.Text type="danger">
                                  <ErrorMessage name="subsidiaryId" />
                                </Typography.Text>
                              </Form.Item>
                            </Col>
                            <Col span={8}>
                              <Form.Item label="Cost Centre Name">
                                <Field name="division">
                                  {({ field, form }: FieldProps) => (
                                    <Input
                                      {...field}
                                      autoComplete="off"
                                    />
                                  )}
                                </Field>
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row>
                            <Col span={12}>
                              <div style={{ textAlign: "left" }}>
                                <Typography.Title
                                  level={4}
                                  style={{ marginBottom: styles.whitespace3 }}
                                >
                                  Cost Centre Admins
                                </Typography.Title>
                              </div>
                            </Col>
                          </Row>
                          {Array.from({ length: props.values.divisionAdmins.length }).map((_, i) => (
                            <Row
                              gutter={16}
                              key={i}
                            >
                              <Col span={9}>
                                <Form.Item label="Name">
                                  <Field name={`divisionAdmins[${i}].name`}>
                                    {({ field, form }: FieldProps) => {
                                      return (
                                        <Select
                                          getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                          {...field}
                                          showSearch
                                          style={{ width: "100%" }}
                                          placeholder="Select a name"
                                          disabled={props.values.recipientLock}
                                          onChange={(value, option: any) => {
                                            const selectedEmail = option ? option.label : "";
                                            form.setFieldValue(`divisionAdmins[${i}].name`, value);
                                            form.setFieldValue(`divisionAdmins[${i}].email`, selectedEmail);

                                            const updatedNames = [...selectedNames, value];
                                            const updatedEmails = [...selectedEmails, selectedEmail];
                                            setSelectedNames(updatedNames);
                                            setSelectedEmails(updatedEmails);

                                            let updatedReviewers = form.values.reviewers.map(
                                              (reviewer: any, index: any) => {
                                                if (index === i) {
                                                  return {
                                                    ...reviewer,
                                                    email: selectedEmail,
                                                    name: value,
                                                  };
                                                }
                                                return reviewer;
                                              }
                                            );

                                            const desiredLength = approverData.length;

                                            if (updatedReviewers.length > desiredLength) {
                                              updatedReviewers = updatedReviewers.slice(0, desiredLength);
                                            }

                                            form.setFieldValue("divisionAdmins", updatedReviewers);
                                          }}
                                          filterOption={(input, option: any) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                          }
                                        >
                                          {allApproveData
                                            ?.filter((user: any) => !adminsName.includes(user.name))
                                            .map((user: any, index: any) => (
                                              <Select.Option
                                                key={index}
                                                value={user.name}
                                                label={user.email}
                                              >
                                                {user.name}
                                              </Select.Option>
                                            ))}
                                        </Select>
                                      );
                                    }}
                                  </Field>
                                  <Typography.Text type="danger">
                                    <ErrorMessage name={`divisionAdmins[${i}].name`} />
                                  </Typography.Text>
                                </Form.Item>
                              </Col>
                              <Col span={9}>
                                <Form.Item label="Email">
                                  <Field name={`divisionAdmins[${i}].email`}>
                                    {({ field, form }: FieldProps) => {
                                      return (
                                        <Select
                                          getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                          {...field}
                                          showSearch
                                          style={{ width: "100%" }}
                                          placeholder="Select an email"
                                          disabled={props.values.recipientLock}
                                          onChange={(value, option: any) => {
                                            const selectedName = option ? option.label : "";
                                            form.setFieldValue(`divisionAdmins[${i}].email`, value);
                                            form.setFieldValue(`divisionAdmins[${i}].name`, selectedName);

                                            // Update state for selected emails and names
                                            const updatedEmails = [...selectedEmails, value];
                                            const updatedNames = [...selectedNames, selectedName];
                                            setSelectedEmails(updatedEmails);
                                            setSelectedNames(updatedNames);

                                            let updatedReviewers = form.values.reviewers.map(
                                              (reviewer: any, index: any) => {
                                                if (index === i) {
                                                  return {
                                                    ...reviewer,
                                                    email: value,
                                                    name: selectedName,
                                                  };
                                                }
                                                return reviewer;
                                              }
                                            );

                                            form.setFieldValue("divisionAdmins", updatedReviewers);
                                          }}
                                          filterOption={(input, option: any) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                          }
                                        >
                                          {allApproveDataByEmail
                                            ?.filter((email: any) => !adminsMail.includes(email.userEmail))
                                            .map((email: any, index: any) => (
                                              <Select.Option
                                                key={index}
                                                value={email.email}
                                                label={email.name}
                                              >
                                                {email.userEmail}
                                              </Select.Option>
                                            ))}
                                        </Select>
                                      );
                                    }}
                                  </Field>
                                  <Typography.Text type="danger">
                                    <ErrorMessage name={`divisionAdmins[${i}].email`} />
                                  </Typography.Text>
                                </Form.Item>
                              </Col>
                              {!viewItem && (
                                <Col span={2}>
                                  <Form.Item label=" ">
                                    <Button
                                      type="link"
                                      danger
                                      disabled={props.values.divisionAdmins.length === 1}
                                      onClick={() => {
                                        const divisionAdmins = [...props.values.divisionAdmins];
                                        divisionAdmins.splice(i, 1);
                                        props.setValues({
                                          ...props.values,
                                          divisionAdmins: divisionAdmins ? divisionAdmins : [],
                                        });
                                      }}
                                    >
                                      {" "}
                                      <Icon
                                        icon="fluent:delete-16-regular"
                                        style={{
                                          color: styles.strawberry,
                                          cursor: "pointer",
                                          fontSize: 20,
                                        }}
                                      />
                                    </Button>
                                  </Form.Item>
                                </Col>
                              )}
                            </Row>
                          ))}

                          {!viewItem && (
                            <Row gutter={16}>
                              <Col
                                span={24}
                                style={{ textAlign: "right", marginBottom: 16 }}
                              >
                                <Button
                                  type="text"
                                  icon={<Icon icon="akar-icons:plus" />}
                                  style={{
                                    cursor: props.values.recipientLock ? "not-allowed" : "auto",
                                  }}
                                  disabled={props.values.divisionAdmins.some(
                                    (val) => val.name === "" || val.email === ""
                                  )}
                                  onClick={() =>
                                    props.setValues({
                                      ...props.values,
                                      divisionAdmins: [
                                        ...props.values.divisionAdmins,
                                        {
                                          name: "",
                                          email: "",
                                        },
                                      ],
                                    })
                                  }
                                >
                                  Add Another
                                </Button>
                              </Col>
                            </Row>
                          )}
                        </div>
                      </div>
                      {!viewItem && (
                        <Row>
                          <Col
                            xs={24}
                            md={24}
                            style={{ textAlign: "right" }}
                          >
                            <Button
                              htmlType="submit"
                              type="primary"
                              disabled={!props.isValid}
                            >
                              Submit
                            </Button>
                          </Col>
                        </Row>
                      )}
                    </Col>
                  </Row>
                </Form>
              </>
            );
          }}
        </Formik>
        <SuccessModal
          message={``}
          title={`You have successfully ${divisionId ? "edited cost centre" : "created a new cost centre"} `}
          type={"success"}
          buttonText={"Go Back"}
          redirect={`/divisions`}
          open={successOpen}
          setOpen={setSuccessOpen}
        />
      </ContentWrapper>
      <Modal
        open={openModal}
        onCancel={onCancel}
        width={400}
        style={{ maxHeight: 300 }}
        centered
        footer={[
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <Button
              onClick={onCancel}
              style={{ marginRight: "10px" }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={onHandleSubmit}
              loading={isSubmitLoading}
            >
              Submit
            </Button>
          </div>,
        ]}
      >
        <div>
          {divisionId ? <p>Are you sure to edit this Cost Centre?</p> : <p>Are you sure to add Cost Centre?</p>}
        </div>
      </Modal>
    </>
  );
};
